"use client";

import { useCallback, useRef, useState } from "react";

export type VaBank = "MANDIRI" | "BCA" | "BRI" | "BNI" | "CIMB" | "PERMATA";

export const VA_BANK_OPTIONS: { code: VaBank; label: string }[] = [
  { code: "MANDIRI", label: "Mandiri" },
  { code: "BCA", label: "BCA" },
  { code: "BRI", label: "BRI" },
  { code: "BNI", label: "BNI" },
  { code: "CIMB", label: "CIMB Niaga" },
  { code: "PERMATA", label: "Permata" },
];

export const DEFAULT_VA_BANK: VaBank = "MANDIRI";

const VA_BANK_CODES = new Set<string>(VA_BANK_OPTIONS.map((bank) => bank.code));

export function isVaBank(value: string): value is VaBank {
  return VA_BANK_CODES.has(value);
}

export interface VirtualAccount {
  bank: VaBank;
  accountNumber: string;
  expiresAt: string | null;
}

/**
 * A number already issued when the checkout was created, handed to the hook as
 * its starting cache so the first paint has something payable in it.
 */
export type InitialVirtualAccount = {
  bank: string;
  accountNumber: string;
  expiresAt: string | null;
};

interface UseVirtualAccountOptions {
  paymentId: string;
  claimToken?: string;
  /** Number issued alongside the checkout, if the server managed to make one. */
  initialAccount?: InitialVirtualAccount | null;
  /** Shown when the number cannot be issued. Comes from the dictionary. */
  errorLabel: string;
}

export interface UseVirtualAccountResult {
  /** The number for the bank asked about, or null while it is being issued. */
  accountFor: (bank: VaBank) => VirtualAccount | null;
  pendingBank: VaBank | null;
  error: string | null;
  issue: (bank: VaBank) => Promise<void>;
}

/**
 * Issues and holds the bank virtual account for a checkout.
 *
 * The number is fetched on demand rather than with the QR, because a VA number
 * comes from a finite bank-assigned range and issuing five per checkout to cover
 * every bank would waste it. The server is idempotent per bank, so re-selecting
 * a bank returns the number already issued instead of a second one.
 */
function seedCache(
  initial: InitialVirtualAccount | null | undefined,
): Partial<Record<VaBank, VirtualAccount>> {
  // The server is the authority on which banks exist, so an unrecognised code
  // is dropped rather than trusted into the cache.
  if (!initial || !isVaBank(initial.bank)) {
    return {};
  }

  return {
    [initial.bank]: {
      bank: initial.bank,
      accountNumber: initial.accountNumber,
      expiresAt: initial.expiresAt,
    },
  };
}

export function useVirtualAccount({
  paymentId,
  claimToken,
  errorLabel,
  initialAccount,
}: UseVirtualAccountOptions): UseVirtualAccountResult {
  // Keyed by bank, so returning to one already fetched is instant and costs no
  // request. The server reuses the same number anyway; this just avoids the
  // round trip and the spinner on a bank the buyer has already seen.
  const [accounts, setAccounts] = useState<
    Partial<Record<VaBank, VirtualAccount>>
  >(() => seedCache(initialAccount));
  const [pendingBank, setPendingBank] = useState<VaBank | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A ref, not state: two calls in the same tick must see the first one's
  // claim, and a state update would not have landed yet.
  const inFlightRef = useRef<VaBank | null>(null);
  // Mirrors `accounts` for the callback to read without taking it as a
  // dependency, which would rebuild `issue` on every fetch and re-run the
  // effect that calls it.
  const cacheRef = useRef<Partial<Record<VaBank, VirtualAccount>>>(
    seedCache(initialAccount),
  );

  const issue = useCallback(
    async (bank: VaBank) => {
      // Already held for this bank: nothing to fetch, and re-requesting would
      // flash the panel back to a loading state for a number we can show now.
      if (cacheRef.current[bank]) {
        return;
      }

      // A request for this bank is already out. React re-runs effects on mount
      // in development and a buyer can double-tap, and each extra request that
      // gets through issues a second live account number for the same payment.
      if (inFlightRef.current === bank) {
        return;
      }

      inFlightRef.current = bank;
      setPendingBank(bank);
      setError(null);

      try {
        const query = claimToken
          ? `?claimToken=${encodeURIComponent(claimToken)}`
          : "";
        const response = await fetch(
          `/api/payment/${paymentId}/virtual-account${query}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bank }),
          },
        );

        if (!response.ok) {
          throw new Error(`Virtual account request failed: ${response.status}`);
        }

        const data = (await response.json()) as {
          bank: VaBank;
          accountNumber: string;
          expiresAt: string | null;
        };

        const issued: VirtualAccount = {
          bank: data.bank,
          accountNumber: data.accountNumber,
          expiresAt: data.expiresAt,
        };

        cacheRef.current = { ...cacheRef.current, [data.bank]: issued };
        setAccounts(cacheRef.current);
      } catch (cause) {
        console.error("Could not issue virtual account", cause);
        // The QR rail is still live underneath, so a failure here is
        // recoverable: the buyer is told to retry or switch to QRIS.
        setError(errorLabel);
      } finally {
        // Only clear the guard if this call still owns it. Switching bank mid
        // request starts a second call, and clearing unconditionally would
        // reopen the guard while that one is still out.
        if (inFlightRef.current === bank) {
          inFlightRef.current = null;
        }
        setPendingBank((current) => (current === bank ? null : current));
      }
    },
    [claimToken, errorLabel, paymentId],
  );

  return {
    accountFor: (bank) => accounts[bank] ?? null,
    pendingBank,
    error,
    issue,
  };
}
